import { Injectable } from "@angular/core";
import { ICustomerModel, IInvoiceModel, IJobModel, IPaymentModel, ISalesTicketModel } from "@models";

// What the dialogs host component can do. Kept as an interface so the customer page can call it
// without importing the host itself - the host's module pulls in the job, invoice, estimate, payment
// and Latham order modules, which in turn import the customer components, so a direct import would
// be a circular NgModule graph.
export interface ICustomerDocumentDialogsHost {
    openJob(job: IJobModel | number): Promise<IJobModel>;
    openQuote(quote: IInvoiceModel | number): Promise<IInvoiceModel>;
    openInvoice(invoice: IInvoiceModel | number): Promise<IInvoiceModel>;
    openPayment(payment: IPaymentModel | number): Promise<IPaymentModel>;
    openLathamOrder(lathamOrderId: number): Promise<void>;

    newJob(customer: ICustomerModel, preferredSalesTicketId?: number): Promise<IJobModel>;
    newQuote(customer: ICustomerModel, preferredSalesTicketId?: number): Promise<IInvoiceModel>;
    newInvoice(customer: ICustomerModel, preferredSalesTicketId?: number): Promise<IInvoiceModel>;
    newPayment(customer: ICustomerModel): Promise<IPaymentModel>;
    newLead(customer: ICustomerModel): Promise<ISalesTicketModel>;
}

/**
 * Lets the customer dashboard open a job, quote, invoice, payment or Latham order in a dialog over
 * the page instead of navigating away to that record's own page.
 *
 * The dialogs themselves live in <customer-document-dialogs>, which the customers page mounts once
 * and which registers itself here. Anything that wants a dialog asks this service; when no host is
 * mounted (the dashboard rendered somewhere the page didn't put the dialogs) `isAvailable` is false
 * and callers fall back to navigating.
 *
 * Every open/new call resolves with the saved model, or null if the dialog was cancelled.
 */
@Injectable({ providedIn: 'root' })
export class CustomerDocumentDialogsService {
    private host: ICustomerDocumentDialogsHost = null;

    register(host: ICustomerDocumentDialogsHost) {
        this.host = host;
    }

    unregister(host: ICustomerDocumentDialogsHost) {
        if (this.host === host)
            this.host = null;
    }

    get isAvailable(): boolean {
        return !!this.host;
    }

    openJob(job: IJobModel | number): Promise<IJobModel> {
        return this.host ? this.host.openJob(job) : Promise.resolve(null);
    }

    openQuote(quote: IInvoiceModel | number): Promise<IInvoiceModel> {
        return this.host ? this.host.openQuote(quote) : Promise.resolve(null);
    }

    openInvoice(invoice: IInvoiceModel | number): Promise<IInvoiceModel> {
        return this.host ? this.host.openInvoice(invoice) : Promise.resolve(null);
    }

    openPayment(payment: IPaymentModel | number): Promise<IPaymentModel> {
        return this.host ? this.host.openPayment(payment) : Promise.resolve(null);
    }

    openLathamOrder(lathamOrderId: number): Promise<void> {
        return this.host ? this.host.openLathamOrder(lathamOrderId) : Promise.resolve();
    }

    newJob(customer: ICustomerModel, preferredSalesTicketId?: number): Promise<IJobModel> {
        return this.host ? this.host.newJob(customer, preferredSalesTicketId) : Promise.resolve(null);
    }

    newQuote(customer: ICustomerModel, preferredSalesTicketId?: number): Promise<IInvoiceModel> {
        return this.host ? this.host.newQuote(customer, preferredSalesTicketId) : Promise.resolve(null);
    }

    newInvoice(customer: ICustomerModel, preferredSalesTicketId?: number): Promise<IInvoiceModel> {
        return this.host ? this.host.newInvoice(customer, preferredSalesTicketId) : Promise.resolve(null);
    }

    newPayment(customer: ICustomerModel): Promise<IPaymentModel> {
        return this.host ? this.host.newPayment(customer) : Promise.resolve(null);
    }

    newLead(customer: ICustomerModel): Promise<ISalesTicketModel> {
        return this.host ? this.host.newLead(customer) : Promise.resolve(null);
    }
}
